import React, { cloneElement, createRef, Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import generateClassesFromMap from '@insight/toolkit-utils/lib/generators/generateClassesFromMap';
import generateUniqueId from '@insight/toolkit-utils/lib/generators/generateUniqueId';

import Portal from '../Portal/Portal';
import { findPopoverPosition, setRef } from '../utils';
import { TOOLTIP_POSITIONS, TOOLTIP_CLASS_MAP } from './classes';
import { DEFAULT_ARROW_STYLES, DEFAULT_TOOLTIP_STYLES } from './constants';
import Popover from './Popover';

export default class Tooltip extends Component {
  anchor = createRef();
  mounted = false;
  popover = createRef();
  state = {
    arrowStyles: DEFAULT_ARROW_STYLES,
    calculatedPosition: this.props.position,
    hasFocus: false,
    id: this.props.id || generateUniqueId(),
    tooltipStyles: DEFAULT_TOOLTIP_STYLES,
    visible: false,
  };

  componentDidMount() {
    this.mounted = true;
  }

  componentDidUpdate(prevProps, { visible }) {
    if (!visible && this.state.visible) {
      window.requestAnimationFrame(this.testAnchor);
    }
  }

  componentWillUnmount() {
    this.mounted = false;
  }

  showToolTip = () => {
    this.setState({ visible: true });
  };

  hideToolTip = () => {
    if (this.mounted) {
      this.setState({ visible: false });
    }
  };

  onBlur = () => {
    this.setState({ hasFocus: false });
    this.hideToolTip();
  };

  onFocus = () => {
    this.setState({ hasFocus: true });
    this.showToolTip();
  };

  onMouseOut = ({ relatedTarget }) => {
    // Prevent Mousing over children from hiding the tooptip by seeing if the mouse left the
    // anchor for a non-child
    const { current } = this.anchor;
    if (current === relatedTarget || !current.contains(relatedTarget)) {
      if (!this.state.hasFocus) {
        this.hideToolTip();
      }
    }

    if (this.props.onMouseOut) {
      this.props.onMouseOut();
    }
  };

  positionTooltip = () => {
    const { arrow, left, position, top } = findPopoverPosition({
      anchor: this.anchor.current,
      arrowConfig: { arrowBuffer: 4, arrowWidth: 12 },
      offset: 16,
      popover: this.popover.current,
      position: this.props.position,
    });
    const tooltipStyles = { left, top };

    this.setState({
      // arrowBaseStyles and arrowLayoverStyles need some work inside findPopoverPosition to return the positions correctly
      arrowBaseStyles: { top: -13, left: arrow.left },
      arrowLayoverStyles: { top: -11, left: arrow.left + 1 },
      calculatedPosition: position,
      tooltipStyles,
      visible: true,
    });
  };

  setPopoverRef = (node) => {
    setRef(this.popover, node);

    // If the popover has been unmounted, clear any cached knowledge about it's size
    if (node == null) {
      this.setState({
        arrowStyles: DEFAULT_ARROW_STYLES,
        tooltipStyles: DEFAULT_TOOLTIP_STYLES,
      });
    }
  };

  testAnchor = () => {
    // When the tooltip is visible, this checks if the anchor is still part of the document
    // This fixes when the root is removed from the dom without first unmounting
    if (!document.body.contains(this.anchor.current)) {
      this.hideToolTip();
    } else if (this.state.visible) {
      window.requestAnimationFrame(this.testAnchor);
    }
  };

  render() {
    const { anchorClassName, children, className, content, title, ...rest } =
      this.props;
    const {
      arrowBaseStyles,
      arrowLayoverStyles,
      calculatedPosition,
      id,
      tooltipStyles,
      visible,
    } = this.state;

    const derivedClasses = generateClassesFromMap(
      { position: calculatedPosition },
      TOOLTIP_CLASS_MAP
    );
    const classes = cn('c-tooltip', derivedClasses, className);
    const anchorClasses = cn('c-tooltip__anchor', anchorClassName);

    const tooltip = visible && (content || title) && (
      <Portal>
        <Popover
          className={classes}
          id={id}
          popoverRef={this.setPopoverRef}
          positionTooltip={this.positionTooltip}
          role="tooltip"
          style={tooltipStyles}
          title={title}
          isInModal={this.props.isInModal}
          {...rest}
        >
          {/* ARROW ELEMENTS ARE HIDDEN UNTIL FURTHER EFFORT CAN BE GIVEN TO HANDLE POSITIONING AS PER BRYAN SCHROTH */}
          {/* <span className="c-tooltip__arrow-base" style={arrowBaseStyles}/>
					<span className="c-tooltip__arrow-overlay" style={arrowLayoverStyles} /> */}
          {content}
        </Popover>
      </Portal>
    );

    const anchor = (
      <span
        className={anchorClasses}
        onMouseOver={this.showToolTip}
        onMouseOut={this.onMouseOut}
        ref={this.anchor}
      >
        {/** We apply the blur and focus methods to the children element because that is the element
         * the user is interacting with, as opposed to the enclosing anchor element. If the inner component
         * is a button and the user tabs to it, we want the enter key to trigger the button. That won't work
         * if the enclosing anchor element has focus
         */}
        {cloneElement(children, {
          'aria-labelledby': id,
          onBlur: this.hideToolTip,
          onFocus: this.showToolTip,
        })}
      </span>
    );

    return (
      <Fragment>
        {anchor}
        {tooltip}
      </Fragment>
    );
  }
}

Tooltip.propTypes = {
  anchorClassName: PropTypes.string,
  children: PropTypes.element.isRequired,
  className: PropTypes.string,
  content: PropTypes.node.isRequired,
  id: PropTypes.string,
  position: PropTypes.oneOf(TOOLTIP_POSITIONS),
  title: PropTypes.node,
};

Tooltip.defaultProps = {
  anchorClassName: '',
  className: '',
  id: '',
  position: 'top',
  title: '',
};
